import { fromPromise } from 'xstate';
import { apolloClient } from '@/lib/apollo';
import {
  PublishScheduleDataMutation,
  RefreshScheduledBusesQuery,
  ViewScheduledBusesQuery,
} from '@/lib/gql/__generated__/canonical-documents';
import type { PublishScheduleDataInput, ScheduledBusResultsScheduleEntries } from '@/lib/gql/__generated__/graphql';

export interface ScheduleDataReadResult {
  entries: Array<ScheduledBusResultsScheduleEntries>;
  lastUpdatedAt: string | null;
}

function normalizeReadResult(result: {
  scheduleEntries?: Array<ScheduledBusResultsScheduleEntries | null> | null;
  lastUpdatedAt?: unknown;
}): ScheduleDataReadResult {
  return {
    entries: (result.scheduleEntries ?? []).filter(
      (entry): entry is ScheduledBusResultsScheduleEntries => entry !== null,
    ),
    lastUpdatedAt: typeof result.lastUpdatedAt === 'string' ? result.lastUpdatedAt : null,
  };
}

export const viewScheduledBusesActor = fromPromise<ScheduleDataReadResult, void>(async () => {
  const { data } = await apolloClient.query({ query: ViewScheduledBusesQuery });
  const result = data?.viewScheduledBuses;
  if (!result) throw new Error('Scheduled buses could not be loaded.');
  return normalizeReadResult(result);
});

export const refreshScheduledBusesActor = fromPromise<ScheduleDataReadResult, void>(async () => {
  const { data } = await apolloClient.query({ query: RefreshScheduledBusesQuery });
  const result = data?.refreshScheduledBuses;
  if (!result) throw new Error('Scheduled buses could not be refreshed.');
  return normalizeReadResult(result);
});

export const publishScheduleDataActor = fromPromise<void, Omit<PublishScheduleDataInput, 'scheduleDataId'>>(
  async ({ input }) => {
    const { data } = await apolloClient.mutate({
      mutation: PublishScheduleDataMutation,
      variables: {
        input: {
          scheduleDataId: crypto.randomUUID(),
          sourceDocumentName: input.sourceDocumentName,
          supportedRoutes: input.supportedRoutes,
          scheduleEntries: input.scheduleEntries,
        },
      },
    });

    const response = data?.publishScheduleData;
    if (!response?.success) {
      throw new Error(response?.error?.message ?? 'The schedule data could not be published.');
    }
  },
);
